import type { GatewayBrowserClient } from "../gateway";

export type McpSetupEnvField = {
  name: string;
  label: string;
};

export type McpSetupRequest = {
  id: string;
  service: string;
  domain: string;
  description: string;
  requiredEnv: McpSetupEnvField[];
  createdAtMs: number;
  expiresAtMs: number;
};

export type McpSetupState = {
  client: GatewayBrowserClient | null;
  mcpSetupQueue: McpSetupRequest[];
  mcpSetupInputs: Record<string, Record<string, string>>;
  mcpSetupBusy: string | null;
};

export function parseMcpSetupRequested(
  payload: Record<string, unknown>,
): McpSetupRequest | null {
  const id = payload.id as string;
  const service = payload.service as string;
  if (!id || !service) return null;
  return {
    id,
    service,
    domain: (payload.domain as string) ?? "",
    description: (payload.description as string) ?? "",
    requiredEnv: (payload.requiredEnv as McpSetupEnvField[]) ?? [],
    createdAtMs: (payload.createdAtMs as number) ?? Date.now(),
    expiresAtMs: (payload.expiresAtMs as number) ?? Date.now() + 300_000,
  };
}

export function addMcpSetupRequest(
  queue: McpSetupRequest[],
  entry: McpSetupRequest,
): McpSetupRequest[] {
  if (queue.some((r) => r.id === entry.id)) return queue;
  return [...queue, entry];
}

export function removeMcpSetupRequest(
  queue: McpSetupRequest[],
  id: string,
): McpSetupRequest[] {
  return queue.filter((r) => r.id !== id);
}

export function updateMcpSetupInput(
  state: McpSetupState,
  requestId: string,
  envName: string,
  value: string,
) {
  const current = state.mcpSetupInputs[requestId] ?? {};
  state.mcpSetupInputs = {
    ...state.mcpSetupInputs,
    [requestId]: { ...current, [envName]: value },
  };
}

export async function submitMcpSetup(
  state: McpSetupState,
  request: McpSetupRequest,
) {
  if (!state.client) return;
  state.mcpSetupBusy = request.id;
  try {
    const env = state.mcpSetupInputs[request.id] ?? {};
    await state.client.request("mcp.setup.resolve", {
      id: request.id,
      env,
    });
  } finally {
    state.mcpSetupBusy = null;
  }
}

export async function cancelMcpSetup(
  state: McpSetupState,
  request: McpSetupRequest,
) {
  if (!state.client) return;
  await state.client.request("mcp.setup.resolve", {
    id: request.id,
    cancelled: true,
  });
}
